
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Check, ChevronRight, CheckCircle } from 'lucide-react';
import { hasRequiredFields } from '@/utils/applicationValidation';
import { useApplicationStatus } from '@/hooks/use-application-status';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { Application } from '@/types/database.types';
import { SECTIONS, VALIDATION_SECTIONS } from '@/constants/application';

interface ApplicationNavProps {
  currentSection: number;
  onSectionChange: (section: number) => void;
  data: Partial<Application>;
}

export const ApplicationNav = ({ currentSection, onSectionChange, data }: ApplicationNavProps) => {
  const [selectedSection, setSelectedSection] = useState(currentSection);
  const { data: applicationStatusData } = useApplicationStatus();
  
  useEffect(() => {
    setSelectedSection(currentSection);
  }, [currentSection]);

  // Convert our SECTIONS array to navigation items
  const NAV_SECTIONS = SECTIONS.map((title, id) => ({
    id,
    title,
    slug: title.toLowerCase().replace(/\s+/g, '-'),
    validation: VALIDATION_SECTIONS[title as keyof typeof VALIDATION_SECTIONS] || title
  }));

  const isDisabled = applicationStatusData?.status === 'submitted' || applicationStatusData?.status === 'approved';

  // Log for debugging
  console.log("ApplicationNav - Checking completion status");
  NAV_SECTIONS.forEach(section => {
    if (section.validation) {
      // Fix for Payroll Schedule - ensure we use the correct validation section name
      const validationSection = section.title === 'Payroll Schedule' ? 'Payroll Schedule' : section.validation;
      const isComplete = hasRequiredFields(validationSection, data);
      console.log(`Section ${section.title} (${validationSection}): ${isComplete ? 'Complete' : 'Incomplete'}`);
      
      // Additional debugging for Workers Comp Assessment
      if (section.title === 'Workers Comp Assessment') {
        console.log('Workers Comp Assessment data:', {
          has_over_road_trucking: data.has_over_road_trucking,
          has_cannabis_hemp_contact: data.has_cannabis_hemp_contact,
          has_elevated_workplace_risk: data.has_elevated_workplace_risk,
          has_company_aircraft: data.has_company_aircraft,
          has_marine_operations: data.has_marine_operations,
          has_heavy_machinery: data.has_heavy_machinery,
          has_recent_wc_claims: data.has_recent_wc_claims,
          has_recent_layoffs: data.has_recent_layoffs,
          has_planned_layoffs: data.has_planned_layoffs,
          has_foreign_travel: data.has_foreign_travel, 
          has_epli_insurance: data.has_epli_insurance,
          has_standalone_epli_policy: data.has_standalone_epli_policy,
          current_workers_comp_carrier: data.current_workers_comp_carrier
        });
      }
      
      // Additional debugging for Payroll Schedule
      if (section.title === 'Payroll Schedule') {
        console.log('Payroll Schedule data:', {
          pay_schedule_type: data.pay_schedule_type,
          pay_period_start: data.pay_period_start,
          pay_period_end: data.pay_period_end,
          next_check_date: data.next_check_date,
          has_bonuses: data.has_bonuses,
          has_commission: data.has_commission,
          validation_section: validationSection
        });
      }
    }
  });

  return (
    <div className="w-full space-y-1 px-3">
      <h3 className="mb-4 px-4 text-sm font-semibold">Application Sections</h3>
      <div className="space-y-1">
        {NAV_SECTIONS.map(section => {
          // Fix for Payroll Schedule - ensure we use the correct validation section name
          const validationSection = section.title === 'Payroll Schedule' ? 'Payroll Schedule' : section.validation;
          const isComplete = hasRequiredFields(validationSection, data);
          const isActive = selectedSection === section.id;
          
          return (
            <Button
              key={section.id}
              variant={isActive ? 'secondary' : 'ghost'}
              className={cn(
                "w-full justify-between",
                isComplete && !isActive && "text-primary"
              )}
              onClick={() => onSectionChange(section.id)}
              disabled={isDisabled}
            >
              <span className="truncate">{section.title}</span>
              {isComplete ? (
                <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0" />
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
